import { useState, useEffect } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Edit2, Trash2 } from "lucide-react";

export default function AdminCustomerTypesPage() {
  const [types, setTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: "", description: "", default_credit_days: 0, default_credit_limit: 0, status: "Active" });

  const load = async () => {
    try { const { data } = await api.get("/admin/customer-types"); setTypes(data); }
    catch(e) { console.error("Load error:", e); } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const openCreate = () => { setEditing(null); setForm({ name: "", description: "", default_credit_days: 0, default_credit_limit: 0, status: "Active" }); setShowDialog(true); };
  const openEdit = (t) => { setEditing(t); setForm({ name: t.name, description: t.description || "", default_credit_days: t.default_credit_days || 0, default_credit_limit: t.default_credit_limit || 0, status: t.status || "Active" }); setShowDialog(true); };

  const save = async () => {
    try {
      if (editing) { await api.put(`/admin/customer-types/${editing.type_id || editing._id}`, form); toast.success("Customer type updated"); }
      else { await api.post("/admin/customer-types", form); toast.success("Customer type created"); }
      setShowDialog(false); load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const fmt = (n) => new Intl.NumberFormat('en-IN').format(n || 0);

  return (
    <div data-testid="admin-customer-types-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight text-zinc-950">Customer Types</h1>
          <p className="text-sm text-zinc-500 mt-1">Classification types for customers</p>
        </div>
        <Button onClick={openCreate} className="rounded-sm h-8 text-xs" data-testid="create-type-btn"><Plus size={14} /> Add Type</Button>
      </div>

      <div className="border border-zinc-200 overflow-hidden">
        <table className="data-table w-full" data-testid="customer-types-table">
          <thead><tr><th>Code</th><th>Name</th><th>Description</th><th className="num">Credit Days</th><th className="num">Credit Limit</th><th>Status</th><th className="w-16">Actions</th></tr></thead>
          <tbody>
            {loading ? <tr><td colSpan={7} className="text-center py-8 text-zinc-400 text-xs">Loading...</td></tr> :
            types.length === 0 ? <tr><td colSpan={7} className="text-center py-8 text-zinc-400 text-xs">No customer types. Create your first type.</td></tr> :
            types.map(t => (
              <tr key={t.type_id || t._id} data-testid={`type-row-${t.type_id}`}>
                <td className="font-mono text-xs text-zinc-500">{t.type_id}</td>
                <td className="font-medium text-zinc-900">{t.name}</td>
                <td className="text-zinc-500 text-xs">{t.description || "-"}</td>
                <td className="num font-mono">{t.default_credit_days}</td>
                <td className="num font-mono">{fmt(t.default_credit_limit)}</td>
                <td><span className={`px-2 py-0.5 text-[10px] font-semibold rounded-sm ${t.status === "Active" ? "status-active" : "status-inactive"}`}>{t.status}</span></td>
                <td>
                  <div className="flex gap-1">
                    <button onClick={() => openEdit(t)} className="p-1 hover:bg-zinc-100 rounded-sm"><Edit2 size={13} className="text-zinc-500" /></button>
                    <button onClick={async () => { if (!window.confirm(`Delete "${t.name}"?`)) return; try { await api.delete(`/admin/customer-types/${t.type_id || t._id}`); toast.success("Deleted"); load(); } catch (err) { toast.error("Delete failed"); } }} className="p-1 hover:bg-red-50 rounded-sm"><Trash2 size={13} className="text-red-400" /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-sm" data-testid="type-dialog">
          <DialogHeader><DialogTitle className="font-heading">{editing ? "Edit Customer Type" : "New Customer Type"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label className="text-xs">Type Name</Label><Input value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} placeholder="e.g. Stockist, Distributor" className="mt-1 rounded-sm" data-testid="type-name-input" /></div>
            <div><Label className="text-xs">Description</Label><Input value={form.description} onChange={(e) => setForm({...form, description: e.target.value})} className="mt-1 rounded-sm" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">Default Credit Days</Label><Input type="number" value={form.default_credit_days} onChange={(e) => setForm({...form, default_credit_days: parseInt(e.target.value) || 0})} className="mt-1 rounded-sm" /></div>
              <div><Label className="text-xs">Default Credit Limit</Label><Input type="number" value={form.default_credit_limit} onChange={(e) => setForm({...form, default_credit_limit: parseFloat(e.target.value) || 0})} className="mt-1 rounded-sm" /></div>
            </div>
            <div>
              <Label className="text-xs">Status</Label>
              <Select value={form.status} onValueChange={(v) => setForm({...form, status: v})}>
                <SelectTrigger className="mt-1 rounded-sm"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="Active">Active</SelectItem><SelectItem value="Inactive">Inactive</SelectItem></SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)} className="rounded-sm text-xs">Cancel</Button>
            <Button onClick={save} className="rounded-sm text-xs" data-testid="save-type-btn">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
